import { Rocket, Zap, TrendingUp, Crown } from "lucide-react";

export const BOOTSTRAP_INTENSIVE = {
  name: "Bootstrap Growth Intensive",
  priceRangeZAR: { min: 6000, max: 25000 },
  priceRangeUSD: { min: 700, max: 1460 },
  priceRangeGBP: { min: 580, max: 1200 },
  description: "Strategic foundation and system setup to build your business growth blueprint.",
  features: [
    "Comprehensive business audit",
    "Ideal customer profiling & targeting",
    "Sales funnel mapping & optimization",
    "CRM setup with WhatsApp integration",
    "Analytics & tracking setup",
    "Email automation foundation",
    "AI assistant workflow planning",
  ],
  note: "One-time mandatory setup fee. Required before starting any Growth Partnership.",
};

export const MONTHLY_PACKAGES = [
  {
    id: "starter",
    name: "Starter / SEO Local",
    icon: Rocket,
    priceZAR: 5999,
    priceUSD: 350,
    priceGBP: 290,
    description: "Perfect for small service businesses or solo entrepreneurs testing digital marketing.",
    target: "Small service businesses, solo entrepreneurs",
    features: [
      { text: "Local SEO setup & monthly optimisation", isContentProduction: false },
      { text: "Google Business Profile management", isContentProduction: false },
      { text: "On-page SEO improvements", isContentProduction: false },
      { text: "Basic Google Ads campaign management", isContentProduction: false },
      { text: "Basic monthly performance reporting", isContentProduction: false },
    ],
    notIncluded: ["CRM automation", "Content creation", "Paid social campaigns"],
    commitment: "Minimum 3-month commitment",
    contentProduction: false,
  },
  {
    id: "growth-engine",
    name: "Growth Engine",
    subtitle: "Foundation Partner",
    icon: Zap,
    priceRangeZAR: { min: 9800, max: 12000 },
    priceRangeUSD: { min: 570, max: 700 },
    priceRangeGBP: { min: 470, max: 580 },
    description: "Comprehensive growth system for businesses ready to scale their marketing.",
    target: "Growing SMEs ready for systematic marketing",
    includesNote: "Includes everything in Starter / SEO Local, plus:",
    features: [
      { text: "Lead generation campaign management", isContentProduction: false },
      { text: "Google Ads management", isContentProduction: false },
      { text: "SEO strategy, blog posts & backlink building", isContentProduction: false },
      { text: "CRM management with WhatsApp integration", isContentProduction: false },
      { text: "Email follow-up sequences", isContentProduction: false },
      { text: "Basic AI chatbot for lead capture", isContentProduction: false },
      { text: "Analytics & performance reporting", isContentProduction: false },
      { text: "1 strategy call per month", isContentProduction: false },
    ],
    contentProduction: false,
  },
  {
    id: "momentum",
    name: "Momentum Builder",
    subtitle: "Growth Accelerator",
    icon: TrendingUp,
    popular: true,
    priceRangeZAR: { min: 15000, max: 22000 },
    priceRangeUSD: { min: 875, max: 1280 },
    priceRangeGBP: { min: 725, max: 1050 },
    description: "Advanced growth acceleration with multi-channel campaigns and full automation.",
    target: "Established businesses scaling aggressively",
    includesNote: "Includes everything in Growth Engine, plus:",
    features: [
      { text: "Meta AND Google Ads management", isContentProduction: false },
      { text: "Lead generation funnel management", isContentProduction: false },
      { text: "Conversion rate optimisation", isContentProduction: false },
      { text: "SEO content strategy", isContentProduction: false },
      { text: "Social media management", isContentProduction: false },
      { text: "Full CRM automation with WhatsApp", isContentProduction: false },
      { text: "AI chatbot/booking assistant", isContentProduction: false },
      { text: "Landing page creation (as required)", isContentProduction: false },
      { text: "Advanced analytics & reporting", isContentProduction: false },
      { text: "Monthly strategy session", isContentProduction: false },
      { text: "2 short videos OR 1 half-day shoot/month (max 4hr production, 6hr post-production)", isContentProduction: true },
    ],
    contentProduction: true,
    contentNote: "Content production included for domestic clients only",
    softwareIncluded: true,
  },
  {
    id: "scale",
    name: "Scale Authority",
    subtitle: "Elite Growth Partner",
    icon: Crown,
    priceRangeZAR: { min: 28000, max: 45000 },
    priceRangeUSD: { min: 1630, max: 2625 },
    priceRangeGBP: { min: 1350, max: 2175 },
    description: "Complete revenue system management for businesses targeting rapid, sustainable growth.",
    target: "Ambitious businesses ready for market dominance",
    includesNote: "Includes everything in Momentum Builder, plus:",
    features: [
      { text: "Multi-channel paid media management", isContentProduction: false },
      { text: "Full funnel optimisation", isContentProduction: false },
      { text: "Complete CRM automation with WhatsApp sequences", isContentProduction: false },
      { text: "Social media management", isContentProduction: false },
      { text: "AI answering/booking assistant", isContentProduction: false },
      { text: "Sales pipeline optimisation", isContentProduction: false },
      { text: "Revenue forecasting & analytics", isContentProduction: false },
      { text: "Content strategy & planning", isContentProduction: false },
      { text: "Bi-weekly strategy sessions", isContentProduction: false },
      { text: "Priority support", isContentProduction: false },
      { text: "1 full production day/month (max 8hr production, 10hr post-production)", isContentProduction: true },
    ],
    contentProduction: true,
    contentNote: "Content production included for domestic clients only",
    softwareIncluded: true,
  },
];

export const SOFTWARE_PLATFORM = {
  name: "OptimaGrowth Software Platform",
  description: "CRM and automation solution powering your growth systems.",
  features: [
    "Pipeline management & deal tracking",
    "WhatsApp business integration",
    "Email & SMS campaign automation",
    "Landing page builder",
    "Workflow automation",
    "AI chatbots",
    "Appointment booking system",
    "Reporting dashboard",
  ],
  pricing: [
    { tier: "Included with Momentum Builder & Scale Authority", price: "Included" },
    { tier: "Optional for Growth Engine clients", priceZAR: 997 },
    { tier: "Standalone subscription (base)", priceZAR: 1497 },
    { tier: "Standalone with AI assistant", priceZAR: 1997 },
  ],
  trialNote: "Optional 30-day free trial available",
};

export const CONTENT_PRODUCTION = {
  title: "Content Production",
  subtitle: "Monthly Video & Photography Production",
  description: "Professional content creation for your marketing campaigns, included with Growth Partnership packages for domestic clients.",
  services: [
    { icon: "Video", name: "Video Production", desc: "Short-form video content, product videos, brand stories" },
    { icon: "Camera", name: "Photography", desc: "Product photography, brand shoots, social media content" },
    { icon: "Clock", name: "Post-Production", desc: "Professional editing, colour grading, motion graphics" },
  ],
  tiers: [
    { name: "Momentum Builder", desc: "2 short videos OR 1 half-day shoot/month", details: "Max 4hr production, 6hr post-production" },
    { name: "Scale Authority", desc: "1 full production day/month", details: "Max 8hr production, 10hr post-production" },
  ],
  note: "Content production is available for domestic (South African) clients only. International clients receive all digital services without content production, reflected in international pricing.",
};

export const getBootstrapPrice = (currency, formatPriceRange) => {
  const ranges = {
    ZAR: BOOTSTRAP_INTENSIVE.priceRangeZAR,
    USD: BOOTSTRAP_INTENSIVE.priceRangeUSD,
    GBP: BOOTSTRAP_INTENSIVE.priceRangeGBP,
  };
  return formatPriceRange(ranges[currency] || ranges.ZAR);
};

export const getPackagePrice = (pkg, currency, currencies) => {
  if (pkg.priceZAR !== undefined) {
    const priceKey = `price${currency}`;
    const price = pkg[priceKey];
    return `${currencies[currency].symbol}${price.toLocaleString()}`;
  }
  const rangeKey = `priceRange${currency}`;
  const range = pkg[rangeKey];
  return `${currencies[currency].symbol}${range.min.toLocaleString()} – ${currencies[currency].symbol}${range.max.toLocaleString()}`;
};

export const getPackageFeatures = (pkg, isInternational) => {
  if (isInternational && pkg.contentProduction) {
    return pkg.features.filter((f) => !f.isContentProduction);
  }
  return pkg.features;
};
